import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";

const EmailDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const load = async () => {
    try {
      const { data } = await axiosInstance.get(`/common/email-logs/${id}`);
      if (data?.success) setEmail(data.data);
      else setError(data?.message || "Not found");
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    document.title = `Email #${id} - Ample Hire`;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const retry = async () => {
    setRetrying(true);
    try {
      const { data } = await axiosInstance.post(`/common/email/${id}/retry`);
      if (data?.success) {
        await load();
      } else {
        alert(data?.message || "Retry failed");
      }
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    } finally { setRetrying(false); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#64748B" }}>Loading…</div>;
  if (error) return (
    <div style={{ maxWidth: 720, margin: "40px auto", padding: 20, background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, color: "#991B1B" }}>
      {error}
      <div><button onClick={() => navigate(-1)} style={{ marginTop: 12, padding: "6px 12px", borderRadius: 6, background: "white", border: "1px solid #E2E8F0", cursor: "pointer" }}>← Back</button></div>
    </div>
  );

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "24px 20px" }}>
      <button onClick={() => navigate(-1)} style={{ fontSize: 12, color: "#7B5AC4", background: "none", border: "none", cursor: "pointer", marginBottom: 12 }}>← Back to inbox</button>

      <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 12, padding: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: "#0F172A", margin: "0 0 12px" }}>{email.subject || "(no subject)"}</h1>

        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "8px 14px", fontSize: 12, marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #F1F5F9" }}>
          <Meta label="To" value={`${email.recipientName ? email.recipientName + " — " : ""}${email.recipientEmail}`} />
          <Meta label="Template" value={email.templateKey || "—"} />
          <Meta label="Status" value={<StatusPill status={email.status} />} />
          {email.providerMessageId && <Meta label="Message ID" value={<code style={{ fontSize: 11 }}>{email.providerMessageId}</code>} />}
          <Meta label="Sent" value={email.sentAt ? new Date(email.sentAt).toLocaleString() : "—"} />
          {email.openedAt && <Meta label="Opened" value={new Date(email.openedAt).toLocaleString()} />}
          {email.repliedAt && <Meta label="Replied" value={new Date(email.repliedAt).toLocaleString()} />}
          {email.bouncedAt && <Meta label="Bounced" value={<span style={{ color: "#DC2626" }}>{new Date(email.bouncedAt).toLocaleString()} · {email.bounceReason}</span>} />}
        </div>

        {(email.status === "BOUNCED" || email.status === "FAILED") && (
          <div style={{ marginBottom: 16, padding: 12, background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 12, color: "#991B1B" }}>
              <strong>Delivery failed.</strong> Click Retry to re-queue for the next dispatcher tick.
            </div>
            <button
              onClick={retry}
              disabled={retrying}
              style={{
                background: "#DC2626",
                color: "white",
                border: "none",
                borderRadius: 6,
                padding: "6px 14px",
                fontSize: 12,
                fontWeight: 600,
                cursor: retrying ? "not-allowed" : "pointer",
                opacity: retrying ? 0.5 : 1,
              }}
            >
              {retrying ? "Retrying…" : "🔄 Retry"}
            </button>
          </div>
        )}

        <div style={{ fontSize: 11, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6, fontWeight: 600 }}>Body</div>
        <div style={{ padding: 16, background: "#F8FAFC", borderRadius: 8, fontSize: 13, color: "#334155", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
          {email.bodyPreview || "(no body preview stored)"}
        </div>

        {email.aiProcessed && (
          <div style={{ marginTop: 16, padding: 12, background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 8 }}>
            <div style={{ fontSize: 11, color: "#7C3AED", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, marginBottom: 6 }}>
              🤖 AI classification
            </div>
            <div style={{ fontSize: 13, color: "#4338CA" }}>
              This email was automatically classified as <strong>{email.aiAction || "processed"}</strong>.
            </div>
          </div>
        )}

        {email.errorMessage && (
          <div style={{ marginTop: 16, padding: 12, background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, fontSize: 12, color: "#991B1B" }}>
            <strong>Error:</strong> {email.errorMessage}
          </div>
        )}

        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #F1F5F9", fontSize: 11, color: "#94A3B8" }}>
          Row #{email.id} · Created {new Date(email.createdAt).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

const Meta = ({ label, value }) => (
  <>
    <div style={{ color: "#94A3B8", fontWeight: 500 }}>{label}</div>
    <div style={{ color: "#0F172A" }}>{value}</div>
  </>
);

const StatusPill = ({ status }) => {
  const styles = {
    SENT: { bg: "#EEF2FF", color: "#4338CA" },
    QUEUED: { bg: "#FEF3C7", color: "#92400E" },
    BOUNCED: { bg: "#FEE2E2", color: "#991B1B" },
    FAILED: { bg: "#FEE2E2", color: "#991B1B" },
  };
  const s = styles[status] || { bg: "#F1F5F9", color: "#475569" };
  return <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 100, background: s.bg, color: s.color, fontWeight: 600 }}>{status}</span>;
};

export default EmailDetail;
